<template>
                <div v-if="currentView === 'backup_settings'">
                    <div class="page-header"><div><div class="page-title">備份管理</div><div class="page-subtitle">設定自動備份排程、目的地與還原操作</div></div></div>

                    <!-- Restore Modal -->
                    <div v-if="showRestoreModal" class="modal-overlay" @click.self="closeRestoreModal">
                        <div class="modal-box" style="max-width:480px;">
                            <div class="modal-header">
                                <span class="modal-title">還原備份</span>
                                <button class="modal-close" @click="closeRestoreModal">✕</button>
                            </div>
                            <div class="modal-body">
                                <div class="text-sm mb-4" style="color:var(--gray-600);">
                                    還原檔案：<span class="font-mono font-bold" style="color:var(--gray-800);">{{ restoreTarget }}</span>
                                </div>
                                <div class="p-3 rounded mb-4" style="background:var(--color-danger-bg,#fef2f2);border:1px solid var(--color-danger,#ef4444);color:var(--color-danger,#ef4444);">
                                    <strong>警告：</strong>還原操作將覆蓋現有資料，建議先備份當前狀態再執行。
                                </div>
                                <div class="space-y-3">
                                    <label class="flex items-center gap-3 cursor-pointer">
                                        <input type="checkbox" v-model="restoreOptions.restore_db" class="w-4 h-4" />
                                        <span class="text-sm font-medium" style="color:var(--gray-700);">還原資料庫（SQLite）</span>
                                    </label>
                                    <label class="flex items-center gap-3 cursor-pointer">
                                        <input type="checkbox" v-model="restoreOptions.restore_mail" class="w-4 h-4" />
                                        <span class="text-sm font-medium" style="color:var(--gray-700);">還原郵件資料（Dovecot）</span>
                                    </label>
                                    <label class="flex items-center gap-3 cursor-pointer">
                                        <input type="checkbox" v-model="restoreOptions.restore_resources" class="w-4 h-4" />
                                        <span class="text-sm font-medium" style="color:var(--gray-700);">還原資源設定</span>
                                    </label>
                                    <label class="flex items-center gap-3 cursor-pointer">
                                        <input type="checkbox" v-model="restoreOptions.restart_services" class="w-4 h-4" />
                                        <span class="text-sm font-medium" style="color:var(--gray-700);">完成後重啟服務</span>
                                    </label>
                                </div>
                            </div>
                            <div class="modal-footer">
                                <button class="btn btn-outline btn-sm" @click="closeRestoreModal">取消</button>
                                <button class="btn btn-danger btn-sm" @click="runRestore" :disabled="restoreLoading">
                                    {{ restoreLoading ? '還原中...' : '確認還原' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="card p-6 mb-6">
                        <div class="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div class="font-bold" style="color:var(--gray-800);">手動備份</div>
                                <div class="text-xs" style="color:var(--gray-500);">備份檔會存放於本機或你設定的目的地</div>
                            </div>
                            <div class="flex items-center gap-2">
                                <button @click="fetchBackupJobs(); fetchBackupFiles(); fetchRestoreJobs()" :disabled="backupJobsLoading || backupFilesLoading"
                                    class="btn btn-dark btn-sm">
                                    {{ (backupJobsLoading || backupFilesLoading) ? '載入中...' : '重新整理' }}
                                </button>
                                <button @click="runBackupNow" :disabled="backupRunLoading"
                                    class="btn btn-primary">
                                    {{ backupRunLoading ? '備份中...' : '立即備份' }}
                                </button>
                            </div>
                        </div>
                        <!-- Running job progress -->
                        <div v-if="backupJobs.some(j => j.status === 'running')" class="mt-4">
                            <div class="flex items-center gap-2 mb-1">
                                <span class="animate-spin text-sm">⟳</span>
                                <span class="text-sm font-medium" style="color:var(--brand-600,#2563eb);">備份進行中...</span>
                                <span class="text-xs" style="color:var(--gray-500);">{{ backupJobs.find(j => j.status === 'running')?.message || '' }}</span>
                            </div>
                            <div class="w-full rounded-full overflow-hidden" style="height:6px;background:var(--gray-200);">
                                <div class="h-full rounded-full animate-pulse" style="width:60%;background:var(--brand-500,#3b82f6);"></div>
                            </div>
                        </div>
                        <div v-if="backupLastRunJobId" class="mt-3 text-xs" style="color:var(--gray-500);">
                            最新工作 ID：<span class="font-mono">{{ backupLastRunJobId }}</span>
                        </div>
                    </div>

                    <div class="card p-6 mb-6">
                        <div class="flex items-center justify-between gap-4 flex-wrap mb-4">
                            <div>
                                <div class="font-bold" style="color:var(--gray-800);">自動備份設定</div>
                                <div class="text-xs" style="color:var(--gray-500);">需要時再啟用，不會預設自動備份</div>
                            </div>
                            <div class="flex items-center gap-2">
                                <button @click="testBackupDestination" :disabled="backupTestLoading"
                                    class="btn btn-dark btn-sm">
                                    {{ backupTestLoading ? '測試中...' : '測試目的地' }}
                                </button>
                                <button @click="saveBackupSettings" :disabled="backupSettingsSaving"
                                    class="btn btn-success">
                                    {{ backupSettingsSaving ? '儲存中...' : '儲存設定' }}
                                </button>
                            </div>
                        </div>

                        <div v-if="backupSettingsLoading" class="text-sm" style="color:var(--gray-500);">載入中...</div>
                        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">啟用自動備份</label>
                                <label class="inline-flex items-center gap-3 cursor-pointer">
                                    <button type="button"
                                        @click="backupSettingsForm.auto_enabled = !backupSettingsForm.auto_enabled"
                                        class="relative inline-flex items-center rounded-full transition-colors flex-shrink-0"
                                        :style="{ width:'44px', height:'24px', background: backupSettingsForm.auto_enabled ? 'var(--brand-500)' : 'var(--gray-300)' }">
                                        <span class="inline-block rounded-full transition-transform"
                                            :style="{ width:'18px', height:'18px', background:'#fff', transform: backupSettingsForm.auto_enabled ? 'translateX(23px)' : 'translateX(3px)', boxShadow:'var(--shadow-sm)' }"></span>
                                    </button>
                                    <span class="text-sm font-medium" style="color:var(--gray-900);">{{ backupSettingsForm.auto_enabled ? '已啟用' : '未啟用' }}</span>
                                </label>
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">備份週期（小時）</label>
                                <input v-model.number="backupSettingsForm.interval_hours" type="number" min="1" max="8760"
                                    class="input" />
                                <div class="text-xs mt-1" style="color:var(--gray-500);">
                                    上次排程時間：{{ backupSettingsForm.last_run_ts ? new Date(Number(backupSettingsForm.last_run_ts) * 1000).toLocaleString() : '—' }}
                                </div>
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">保留最近幾份（Keep last）</label>
                                <input v-model.number="backupSettingsForm.keep_last" type="number" min="0" max="500"
                                    class="input" />
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">備份目的地</label>
                                <select v-model="backupSettingsForm.destination" class="input">
                                    <option value="local">本機</option>
                                    <option value="nas">NAS</option>
                                    <option value="gdrive">Google Drive (rclone)</option>
                                </select>
                            </div>
                        </div>

                        <div v-if="backupSettingsForm.destination === 'nas'" class="mt-6 border-t pt-6">
                            <div class="font-bold mb-4" style="color:var(--gray-800);">NAS 設定</div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">NAS 掛載路徑（nas_path）</label>
                                    <input v-model="backupSettingsForm.nas_path" type="text" class="input" placeholder="例如：/mnt/nas 或 \\\\NAS\\share" />
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">NAS 類型（nas_type）</label>
                                    <select v-model="backupSettingsForm.nas_type" class="input">
                                        <option value="cifs">cifs</option>
                                        <option value="nfs">nfs</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">NAS IP（選填）</label>
                                    <input v-model="backupSettingsForm.nas_ip" type="text" class="input" placeholder="例如：192.168.1.10" />
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Share（選填）</label>
                                    <input v-model="backupSettingsForm.nas_share" type="text" class="input" placeholder="例如：share" />
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">帳號（選填）</label>
                                    <input v-model="backupSettingsForm.nas_username" type="text" class="input" />
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">密碼（留空或保持遮罩可沿用原值）</label>
                                    <input v-model="backupSettingsForm.nas_password" type="password" class="input" autocomplete="off" />
                                    <div v-if="backupSettingsForm.nas_password_set" class="text-xs mt-1" style="color:var(--gray-500);">目前已有設定密碼</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="backupSettingsForm.destination === 'gdrive'" class="mt-6 border-t pt-6">
                            <div class="font-bold mb-4" style="color:var(--gray-800);">Google Drive（rclone）設定</div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">Remote（gdrive_remote）</label>
                                    <input v-model="backupSettingsForm.gdrive_remote" type="text" class="input" placeholder="例如：mydrive:" />
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">資料夾（gdrive_folder）</label>
                                    <input v-model="backupSettingsForm.gdrive_folder" type="text" class="input" placeholder="例如：ai_mail_backups" />
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">rclone config 路徑（選填）</label>
                                    <input v-model="backupSettingsForm.gdrive_config_path" type="text" class="input" placeholder="例如：/root/.config/rclone/rclone.conf" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card p-6 mb-6">
                        <div class="flex items-center justify-between gap-4 flex-wrap mb-4">
                            <div class="font-bold" style="color:var(--gray-800);">備份工作（Jobs）</div>
                            <button @click="fetchBackupJobs" :disabled="backupJobsLoading"
                                class="btn btn-dark btn-sm">
                                {{ backupJobsLoading ? '載入中...' : '重新整理' }}
                            </button>
                        </div>
                        <div class="overflow-x-auto">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th class="px-3 py-2">時間</th>
                                        <th class="px-3 py-2">狀態</th>
                                        <th class="px-3 py-2">來源</th>
                                        <th class="px-3 py-2">檔名</th>
                                        <th class="px-3 py-2">大小</th>
                                        <th class="px-3 py-2">訊息</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="backupJobs.length === 0">
                                        <td colspan="6" class="px-3 py-6 text-center" style="color:var(--gray-500);">尚無工作紀錄</td>
                                    </tr>
                                    <tr v-for="j in backupJobs" :key="j.id" class="border-t">
                                        <td class="px-3 py-2 whitespace-nowrap" style="color:var(--gray-600);">{{ j.created_at ? new Date(Number(j.created_at) * 1000).toLocaleString() : '—' }}</td>
                                        <td class="px-3 py-2 whitespace-nowrap">
                                            <span class="badge"
                                                :class="j.status === 'done' ? 'badge-success' : (j.status === 'running' ? 'badge-info' : (j.status === 'failed' ? 'badge-danger' : ''))">
                                                {{ j.status || '—' }}
                                            </span>
                                        </td>
                                        <td class="px-3 py-2 whitespace-nowrap" style="color:var(--gray-600);">{{ j.triggered_by || '—' }}</td>
                                        <td class="px-3 py-2 font-mono" style="color:var(--gray-700);">{{ j.filename || '—' }}</td>
                                        <td class="px-3 py-2 whitespace-nowrap" style="color:var(--gray-600);">{{ j.size ? formatBytes(j.size) : '—' }}</td>
                                        <td class="px-3 py-2" style="color:var(--gray-600);">{{ j.message || '—' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="card p-6">
                        <div class="flex items-center justify-between gap-4 flex-wrap mb-4">
                            <div class="font-bold" style="color:var(--gray-800);">備份檔案</div>
                            <button @click="fetchBackupFiles" :disabled="backupFilesLoading"
                                class="btn btn-dark btn-sm">
                                {{ backupFilesLoading ? '載入中...' : '重新整理' }}
                            </button>
                        </div>
                        <div class="overflow-x-auto">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th class="px-3 py-2">檔名</th>
                                        <th class="px-3 py-2">大小</th>
                                        <th class="px-3 py-2">修改時間</th>
                                        <th class="px-3 py-2">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="backupFiles.length === 0">
                                        <td colspan="4" class="px-3 py-6 text-center" style="color:var(--gray-500);">尚無備份檔案</td>
                                    </tr>
                                    <tr v-for="f in backupFiles" :key="f.filename" class="border-t">
                                        <td class="px-3 py-2 font-mono" style="color:var(--gray-700);">{{ f.filename }}</td>
                                        <td class="px-3 py-2 whitespace-nowrap" style="color:var(--gray-600);">{{ formatBytes(f.size) }}</td>
                                        <td class="px-3 py-2 whitespace-nowrap" style="color:var(--gray-600);">{{ f.mtime ? new Date(Number(f.mtime) * 1000).toLocaleString() : '—' }}</td>
                                        <td class="px-3 py-2 whitespace-nowrap flex gap-2">
                                            <button @click="downloadBackupFile(f.filename)"
                                                class="btn btn-primary btn-xs">
                                                下載
                                            </button>
                                            <button @click="openRestoreModal(f.filename)"
                                                class="btn btn-danger btn-xs">
                                                還原
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Restore Jobs -->
                    <div v-if="restoreJobs.length > 0" class="card p-6 mt-6">
                        <div class="flex items-center justify-between gap-4 flex-wrap mb-4">
                            <div class="font-bold" style="color:var(--gray-800);">還原工作紀錄</div>
                            <button @click="fetchRestoreJobs" :disabled="restoreJobsLoading"
                                class="btn btn-dark btn-sm">
                                {{ restoreJobsLoading ? '載入中...' : '重新整理' }}
                            </button>
                        </div>
                        <div v-for="j in restoreJobs" :key="j.id" class="border-b py-3" style="border-color:var(--border);">
                            <div class="flex items-center gap-3 flex-wrap">
                                <span class="badge"
                                    :class="j.status === 'done' ? 'badge-success' : (j.status === 'running' || j.status === 'queued' ? 'badge-info' : 'badge-danger')">
                                    {{ j.status }}
                                </span>
                                <span class="font-mono text-xs" style="color:var(--gray-700);">{{ j.filename }}</span>
                                <span class="text-xs" style="color:var(--gray-500);">{{ j.created_at ? new Date(Number(j.created_at) * 1000).toLocaleString() : '' }}</span>
                                <span class="text-xs" style="color:var(--gray-600);">{{ j.message || '' }}</span>
                            </div>
                            <div v-if="j.status === 'running' || j.status === 'queued'" class="mt-2">
                                <div class="w-full rounded-full overflow-hidden" style="height:5px;background:var(--gray-200);">
                                    <div class="h-full rounded-full animate-pulse" style="width:50%;background:var(--color-warning,#f59e0b);"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Security Filter View -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  backupSettingsForm, backupSettingsLoading, backupSettingsSaving,
  backupTestLoading, backupRunLoading, backupLastRunJobId,
  backupJobsLoading, backupFilesLoading,
  backupJobs, backupFiles,
  showRestoreModal, restoreTarget, restoreOptions,
  restoreLoading, restoreJobId, restoreJobs, restoreJobsLoading,
  isSuperAdmin,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  formatBytes, testBackupDestination, runBackupNow,
  fetchBackupJobs, fetchBackupFiles, downloadBackupFile,
  openRestoreModal, closeRestoreModal, runRestore, fetchRestoreJobs,
} = store
</script>

